/**
 *
 * ReportForm
 *
 */
import { Fragment } from 'react';

import type { Language } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { HookForm, Number, Text, Translable } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { useTranslateList } from 'app/components/translate/useTranslateList';
import { factoryYup, limit } from 'app/core/factory-yup';
import { notificationSuccess } from 'app/core/toast';
import Env from 'env';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '../../../../../core/dialog';
import { messages } from '../../../../Operations/EconomyActivity/translation';
import { label as labelCommon } from '../../../../translation';
import { type Report } from '../../models/types';
import { createReport, deleteReport, updateReport } from '../../services/http';
import { label } from '../../translation';

const yup = factoryYup();
interface Props {
  defaultValue: Report;
}

const validations = yup.object().shape({
  name: yup.string().required(i18next.t('reports.validations.nameRequired')).max(limit.maxName),
  shortName: yup
    .string()
    .required(i18next.t('reports.validations.shortNameRequired'))
    .max(limit.maxName),
  code: yup
    .number()
    .nullable()
    .required(i18next.t('reports.validations.codeRequired'))
    .min(limit.minCode)
});

interface ReportRequest extends Report {
  shortNameTranslate: Array<Language>;
  nameTranslate: Array<Language>;
}

export function ReportForm(props: Props) {
  const availableLanguages = useTranslateList();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const { defaultValue } = props;

  const [nameTranslate] = defaultValue.translations?.filter((d) => d.key === 'name') || [];
  const [shortNameTranslate] =
    defaultValue.translations?.filter((d) => d.key === 'shortName') || [];

  const initialValue: ReportRequest = {
    ...defaultValue,
    nameTranslate: nameTranslate?.languages,
    shortNameTranslate: shortNameTranslate?.languages
  };

  const saveTax = async (reportRequest: ReportRequest) => {
    reportRequest.id = defaultValue.id;

    const { nameTranslate, shortNameTranslate, ...newReport } = reportRequest;
    const payload: Report = {
      ...newReport,
      translations: [
        { key: 'name', languages: nameTranslate },
        { key: 'shortName', languages: shortNameTranslate }
      ]
    };
    try {
      if (payload.id) {
        await updateReport(payload.id, payload);
      } else {
        await createReport(payload);
      }
      notificationSuccess(i18next.t('reports.messages.savedSuccessfully'));
      navigate('/taxes/manage/complementary');
    } catch (e) {
      console.error(e);
    }
  };

  const onCancel = () => {
    navigate('/taxes/manage/complementary');
  };

  return (
    <Fragment>
      <HookForm initialValues={initialValue} validations={validations} onSubmit={saveTax}>
        <Row>
          <Col>
            <Number label={label(t, 'code')} name={'code'} />
          </Col>
        </Row>
        <Row>
          <Col>
            <Text label={label(t, 'shortName')} name={'shortName'} />
            <Translable
              propertyTranslate="shortName"
              name="shortNameTranslate"
              defaultValues={initialValue.shortNameTranslate}
              availableLanguages={availableLanguages}
              defaultLang={Env.defaultLang}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Text label={label(t, 'name')} name={'name'} />
            <Translable
              propertyTranslate="name"
              name="nameTranslate"
              defaultValues={initialValue.nameTranslate}
              availableLanguages={availableLanguages}
              defaultLang={Env.defaultLang}
            />
          </Col>
        </Row>

        <BtnToolbar>
          <BtnGroup>
            <Button action={ButtonAction.cancel} variant="secondary" onClick={onCancel} />

            <Button
              visible={initialValue?.id !== undefined}
              action={ButtonAction.delete}
              variant={'danger'}
              onClick={async () => {
                const ok = await ConfirmDialog({
                  titleModal: messages(t, 'confirmTitle'),
                  message: messages(t, 'confirmDeleted'),
                  okButton: labelCommon('ok'),
                  cancelButton: labelCommon('cancel')
                });
                if (ok) {
                  try {
                    await deleteReport(defaultValue.id as string);
                    notificationSuccess(i18next.t('reports.messages.deletedSuccessfully'));
                    navigate('/taxes/manage/complementary');
                  } catch (e) {
                    console.error(e);
                  }
                }
              }}
            />
          </BtnGroup>

          <BtnGroup>
            <Button action={ButtonAction.save} variant="primary" type={'submit'} />
          </BtnGroup>
        </BtnToolbar>
      </HookForm>
    </Fragment>
  );
}
